import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  bulkDeleteCards,
  createCard,
  createDeck,
  deleteDeck,
  getDeckStats,
  insertCardsInChunks,
  listCards,
  listDecks,
  normalizeImportRows,
  parseImportFile,
  updateCard,
} from '../services/flashcardsService';

const Flashcards = () => {
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();

  const [decks, setDecks] = React.useState([]);
  const [decksLoading, setDecksLoading] = React.useState(true);
  const [decksError, setDecksError] = React.useState('');
  const [selectedDeckId, setSelectedDeckId] = React.useState('');

  const [cards, setCards] = React.useState([]);
  const [cardsLoading, setCardsLoading] = React.useState(true);
  const [cardsError, setCardsError] = React.useState('');

  const [searchText, setSearchText] = React.useState('');
  const [deckStats, setDeckStats] = React.useState({ newCount: 0, dueCount: 0, learnedCount: 0, totalCount: 0 });
  const [revealedCardIds, setRevealedCardIds] = React.useState(() => new Set());
  const [selectedCardIds, setSelectedCardIds] = React.useState(() => new Set());

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const [upgradePrompt, setUpgradePrompt] = React.useState(null);

  const [createDeckOpen, setCreateDeckOpen] = React.useState(false);
  const [newDeckName, setNewDeckName] = React.useState('');
  const [newDeckDescription, setNewDeckDescription] = React.useState('');
  const [newDeckFrontLang, setNewDeckFrontLang] = React.useState(localStorage.getItem('sourceLang') || '');
  const [newDeckBackLang, setNewDeckBackLang] = React.useState(localStorage.getItem('targetLang') || '');
  const [creatingDeck, setCreatingDeck] = React.useState(false);
  const [deletingDeck, setDeletingDeck] = React.useState(false);

  const [createCardOpen, setCreateCardOpen] = React.useState(false);
  const [creatingCard, setCreatingCard] = React.useState(false);
  const [newCardFront, setNewCardFront] = React.useState('');
  const [newCardBack, setNewCardBack] = React.useState('');
  const [newCardFrontImageUrl, setNewCardFrontImageUrl] = React.useState('');
  const [newCardBackImageUrl, setNewCardBackImageUrl] = React.useState('');

  const [importOpen, setImportOpen] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [importFile, setImportFile] = React.useState(null);
  const [importHeaders, setImportHeaders] = React.useState([]);
  const [importRows, setImportRows] = React.useState([]);
  const [importMapping, setImportMapping] = React.useState({
    front: '',
    back: '',
    frontImageUrl: '',
    backImageUrl: '',
    tags: '',
  });
  const [importPreviewCount] = React.useState(10);

  const [editingCard, setEditingCard] = React.useState(null);
  const [editFront, setEditFront] = React.useState('');
  const [editBack, setEditBack] = React.useState('');
  const [editFrontImg, setEditFrontImg] = React.useState('');
  const [editBackImg, setEditBackImg] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  React.useEffect(() => {
    if (editingCard) {
      setEditFront(editingCard.front_text || '');
      setEditBack(editingCard.back_text || '');
      setEditFrontImg(editingCard.front_image_url || '');
      setEditBackImg(editingCard.back_image_url || '');
    }
  }, [editingCard]);

  const handleSaveEdit = async () => {
    if (!editingCard) return;
    setSaving(true);
    try {
      const { error } = await updateCard(user, editingCard.id, {
        front_text: editFront.trim(),
        back_text: editBack.trim(),
        front_image_url: editFrontImg.trim() || null,
        back_image_url: editBackImg.trim() || null,
      });
      if (error) {
        showSnackbar(error, 'error');
        return;
      }
      showSnackbar('Card updated!');
      setEditingCard(null);
      loadCards();
    } finally {
      setSaving(false);
    }
  };

  const loadDecks = React.useCallback(async () => {
    setDecksLoading(true);
    setDecksError('');
    try {
      const { data, error } = await listDecks(user);
      if (error) {
        setDecksError(error);
        setDecks([]);
        return;
      }
      setDecks(data);
      setSelectedDeckId((prev) => {
        if (prev && (data || []).some((d) => d.id === prev)) return prev;
        if (data && data.length > 0) return data[0].id;
        return '';
      });
    } finally {
      setDecksLoading(false);
    }
  }, [user]);

  const loadCards = React.useCallback(async () => {
    setCardsLoading(true);
    setCardsError('');
    try {
      const { data, error } = await listCards(user, {
        deckId: selectedDeckId || null,
        searchText,
      });
      if (error) {
        setCardsError(error);
        setCards([]);
        return;
      }
      setCards(data);
      const visibleIds = new Set((data || []).map((c) => c.id));
      setSelectedCardIds((prev) => {
        const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
        return next;
      });
    } finally {
      setCardsLoading(false);
    }
  }, [user, selectedDeckId, searchText]);

  React.useEffect(() => {
    loadDecks();
  }, [loadDecks]);

  React.useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Fetch deck stats whenever deck or cards change
  React.useEffect(() => {
    if (!user || !selectedDeckId) {
      setDeckStats({ newCount: 0, dueCount: 0, learnedCount: 0, totalCount: 0 });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await getDeckStats(user, selectedDeckId);
      if (!cancelled && data) setDeckStats(data);
    })();
    return () => { cancelled = true; };
  }, [user, selectedDeckId, cards]);

  const toggleReveal = (cardId) => {
    setRevealedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const toggleSelected = (cardId) => {
    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedCardIds(new Set());
  };

  const allVisibleSelected = cards.length > 0 && selectedCardIds.size === cards.length;
  const someVisibleSelected = selectedCardIds.size > 0 && selectedCardIds.size < cards.length;

  const handleToggleSelectAllVisible = () => {
    if (cards.length === 0) return;
    if (allVisibleSelected) {
      clearSelection();
      return;
    }
    setSelectedCardIds(new Set(cards.map((c) => c.id)));
  };

  const handleDeleteDeck = async () => {
    if (!selectedDeckId) return;
    const deck = decks.find((d) => d.id === selectedDeckId);
    const label = deck?.name ? `"${deck.name}"` : 'this deck';
    if (!window.confirm(`Delete ${label} and all its flashcards?`)) return;

    setDeletingDeck(true);
    try {
      const { error } = await deleteDeck(user, selectedDeckId);
      if (error) {
        showSnackbar(error, 'error');
        return;
      }

      setRevealedCardIds(new Set());
      clearSelection();
      setSelectedDeckId('');
      await loadDecks();
      showSnackbar('Deck deleted');
    } finally {
      setDeletingDeck(false);
    }
  };

  const handleCreateDeck = async () => {
    if (!newDeckName.trim()) {
      showSnackbar('Deck name is required', 'error');
      return;
    }

    setCreatingDeck(true);
    try {
      const { data, error } = await createDeck(user, {
        name: newDeckName,
        description: newDeckDescription,
        front_language: newDeckFrontLang || null,
        back_language: newDeckBackLang || null,
      });

      if (error) {
        showSnackbar(error, 'error');
        return;
      }

      setCreateDeckOpen(false);
      setNewDeckName('');
      setNewDeckDescription('');
      setNewDeckFrontLang(localStorage.getItem('sourceLang') || '');
      setNewDeckBackLang(localStorage.getItem('targetLang') || '');

      await loadDecks();
      if (data?.id) {
        setSelectedDeckId(data.id);
      }

      showSnackbar('Deck created');
    } finally {
      setCreatingDeck(false);
    }
  };

  const handleCreateCard = async () => {
    if (!selectedDeckId) {
      showSnackbar('Create or select a deck first', 'error');
      return;
    }
    if (!newCardFront.trim()) {
      showSnackbar('Front text is required', 'error');
      return;
    }

    setCreatingCard(true);
    try {
      const result = await createCard(user, {
        deck_id: selectedDeckId,
        front_text: newCardFront,
        back_text: newCardBack,
        front_image_url: newCardFrontImageUrl,
        back_image_url: newCardBackImageUrl,
      });

      if (result?.code === 'FLASHCARDS_DAILY_CAP_REACHED') {
        setUpgradePrompt({
          title: 'Daily limit reached',
          message: result?.error || 'Free plan daily flashcard limit reached. Upgrade to create unlimited cards.',
        });
        return;
      }

      if (result?.error) {
        showSnackbar(result.error, 'error');
        return;
      }

      setCreateCardOpen(false);
      setNewCardFront('');
      setNewCardBack('');
      setNewCardFrontImageUrl('');
      setNewCardBackImageUrl('');

      await loadCards();
      showSnackbar('Flashcard created');
    } finally {
      setCreatingCard(false);
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedCardIds);
    if (ids.length === 0) return;

    if (!window.confirm(`Delete ${ids.length} selected flashcard(s)?`)) {
      return;
    }

    const { error } = await bulkDeleteCards(user, ids);
    if (error) {
      showSnackbar(error, 'error');
      return;
    }

    clearSelection();
    await loadCards();
    showSnackbar('Deleted');
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportHeaders([]);
    setImportRows([]);
    setImportMapping({
      front: '',
      back: '',
      frontImageUrl: '',
      backImageUrl: '',
      tags: '',
    });
  };

  const handlePickImportFile = async (file) => {
    if (!file) return;
    setImportFile(file);

    try {
      const parsed = await parseImportFile(file);
      const headers = parsed?.headers || [];
      const rows = parsed?.rows || [];
      setImportHeaders(headers);
      setImportRows(rows);

      setImportMapping((prev) => ({
        ...prev,
        front: headers[0] || '',
        back: headers[1] || '',
      }));
    } catch (e) {
      console.error('Import parse failed:', e);
      showSnackbar(e?.message || 'Failed to parse file', 'error');
      resetImportState();
    }
  };

  const handleImport = async () => {
    if (!selectedDeckId) {
      showSnackbar('Create or select a deck first', 'error');
      return;
    }
    if (!importFile) {
      showSnackbar('Pick a file first', 'error');
      return;
    }
    if (!importMapping.front) {
      showSnackbar('Map a front column', 'error');
      return;
    }

    setImporting(true);
    try {
      const cardsToInsert = normalizeImportRows({ headers: importHeaders, rows: importRows }, importMapping);
      if (!cardsToInsert.length) {
        showSnackbar('No rows found to import', 'error');
        return;
      }

      const result = await insertCardsInChunks(user, {
        deckId: selectedDeckId,
        cards: cardsToInsert,
        chunkSize: 150,
        dedupe: true,
      });

      if (result?.code === 'FLASHCARDS_DAILY_CAP_REACHED') {
        setUpgradePrompt({
          title: 'Daily limit reached',
          message: result?.error || 'Free plan daily flashcard limit reached. Upgrade to create unlimited cards.',
        });
        return;
      }

      if (result?.error) {
        showSnackbar(result.error, 'error');
        return;
      }

      const skipped = result?.skippedDueToCap || 0;
      if (skipped > 0) {
        showSnackbar(`Imported ${result.inserted}. Skipped ${skipped} due to daily Free cap.`, 'info');
      } else {
        showSnackbar(`Imported ${result.inserted}`);
      }

      setImportOpen(false);
      resetImportState();
      await loadCards();
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* ── Hero: Practice CTA ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2.5, md: 3 },
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
          border: '1px solid',
          borderColor: 'rgba(102, 126, 234, 0.15)',
          borderRadius: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Flashcards
          </Typography>
          {!isPremium && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Free plan: 10 new flashcards/day.{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
                onClick={() => navigate('/usage?plan=yearly')}
              >
                Upgrade for unlimited
              </Typography>
            </Typography>
          )}
        </Box>

        <Stack spacing={1.5} alignItems="flex-end">
          {/* Deck stats chips */}
          {selectedDeckId && deckStats.totalCount > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent="flex-end">
              {deckStats.newCount > 0 && (
                <Chip label={`${deckStats.newCount} new`} size="small" sx={{ fontWeight: 600, bgcolor: 'info.main', color: 'info.contrastText' }} />
              )}
              {deckStats.dueCount > 0 && (
                <Chip label={`${deckStats.dueCount} due`} size="small" sx={{ fontWeight: 600, bgcolor: 'warning.main', color: 'warning.contrastText' }} />
              )}
              {deckStats.learnedCount > 0 && (
                <Chip label={`${deckStats.learnedCount} learned`} size="small" sx={{ fontWeight: 600, bgcolor: 'success.main', color: 'success.contrastText' }} />
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={1.5}>
            {/* Review Due button */}
            {(deckStats.dueCount > 0 || deckStats.newCount > 0) && (
              <Button
                variant="outlined"
                onClick={() => navigate(`/flashcards/review?deckId=${encodeURIComponent(selectedDeckId)}&mode=due`)}
                disabled={!selectedDeckId}
                sx={{
                  fontWeight: 700,
                  borderRadius: 3,
                  borderWidth: 2,
                  borderColor: 'warning.main',
                  color: 'warning.dark',
                  '&:hover': { borderWidth: 2, borderColor: 'warning.dark', bgcolor: 'warning.50' },
                  px: 3,
                  py: 1.2,
                }}
              >
                Review Due ({deckStats.dueCount + deckStats.newCount})
              </Button>
            )}

            {/* Practice All button */}
            <Button
              variant="contained"
              onClick={() => navigate(`/flashcards/review?deckId=${encodeURIComponent(selectedDeckId)}${searchText ? `&search=${encodeURIComponent(searchText)}` : ''}`)}
              disabled={!selectedDeckId || cards.length === 0}
              startIcon={<PlayArrowIcon />}
              sx={(theme) => {
                const dark = theme.palette.mode === 'dark';
                return {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    boxShadow: '0 8px 35px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px) scale(1.02)',
                  },
                  '&:disabled': {
                    background: dark ? 'rgba(255,255,255,0.08)' : '#e0e0e0',
                    color: dark ? 'rgba(255,255,255,0.3)' : undefined,
                    boxShadow: 'none',
                    transform: 'none',
                  },
                  fontWeight: 800,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  letterSpacing: 0.5,
                  px: { xs: 4, md: 5 },
                  py: { xs: 1.4, md: 1.6 },
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4), 0 0 60px rgba(118, 75, 162, 0.15)',
                  transition: 'all 0.25s ease',
                  animation: (!selectedDeckId || cards.length === 0) ? 'none' : 'practiceGlow 2s ease-in-out infinite',
                  '@keyframes practiceGlow': {
                    '0%, 100%': {
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4), 0 0 60px rgba(118, 75, 162, 0.15)',
                    },
                    '50%': {
                      boxShadow: '0 8px 40px rgba(102, 126, 234, 0.65), 0 0 80px rgba(118, 75, 162, 0.3)',
                    },
                  },
                  minWidth: 200,
                };
              }}
            >
              {cards.length > 0 ? `Practice ${cards.length} card${cards.length === 1 ? '' : 's'}!` : 'Practice'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ── Toolbar ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        {/* Row 1: Deck management + Search + Add cards */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
        >
          {/* Deck selector group */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: { md: '0 0 auto' }, minWidth: { md: 280 } }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}>
              Deck
            </Typography>
            <FormControl
              size="small"
              disabled={decksLoading}
              sx={{
                minWidth: 140,
                flex: 1,
                ...(!selectedDeckId && !decksLoading ? {
                  '& .MuiOutlinedInput-root': {
                    borderColor: '#667eea',
                    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.3), 0 0 20px rgba(102, 126, 234, 0.15)',
                    animation: 'deckSelectPulse 2s ease-in-out infinite',
                  },
                  '@keyframes deckSelectPulse': {
                    '0%, 100%': {
                      boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.3), 0 0 20px rgba(102, 126, 234, 0.15)',
                    },
                    '50%': {
                      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.5), 0 0 30px rgba(118, 75, 162, 0.25)',
                    },
                  },
                  borderRadius: 1,
                } : {}),
              }}
            >
              <Select
                displayEmpty
                value={selectedDeckId}
                onChange={(e) => {
                  setSelectedDeckId(e.target.value);
                  clearSelection();
                  setRevealedCardIds(new Set());
                }}
                renderValue={(val) => {
                  if (!val) return <em style={{ color: '#667eea', fontWeight: 600 }}>👆 Select a deck to start</em>;
                  const d = decks.find((dk) => dk.id === val);
                  return d?.name || val;
                }}
              >
                {decks.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Create new deck" arrow>
              <IconButton
                size="small"
                onClick={() => setCreateDeckOpen(true)}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(102, 126, 234, 0.08)' },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete this deck" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!selectedDeckId || deletingDeck}
                  onClick={handleDeleteDeck}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    '&:hover': { borderColor: 'error.main', color: 'error.main', bgcolor: 'rgba(211, 47, 47, 0.08)' },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search cards..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 120 }}
          />

          {/* Card actions */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: { md: '0 0 auto' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportOpen(true)}
              disabled={!selectedDeckId}
              sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}
            >
              Import
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateCardOpen(true)}
              disabled={!selectedDeckId}
              sx={(theme) => {
                const dark = theme.palette.mode === 'dark';
                return {
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
                  },
                  '&:disabled': {
                    background: dark ? 'rgba(255,255,255,0.08)' : '#e0e0e0',
                    color: dark ? 'rgba(255,255,255,0.3)' : undefined,
                    boxShadow: 'none',
                  },
                };
              }}
            >
              New Card
            </Button>
          </Stack>
        </Stack>

        {/* Row 2: Selection bar — only visible when cards exist */}
        {cards.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                sx={{ mr: 0.5 }}
                label={<Typography variant="body2" color="text.secondary">Select all</Typography>}
                control={(
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={handleToggleSelectAllVisible}
                    disabled={cardsLoading || cards.length === 0}
                    inputProps={{ 'aria-label': 'select all flashcards' }}
                  />
                )}
              />
              {selectedCardIds.size > 0 && (
                <>
                  <Chip
                    label={`${selectedCardIds.size} selected`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSelected}
                    sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}
                  >
                    Delete selected
                  </Button>
                </>
              )}
            </Stack>
          </>
        )}

        {decksError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {decksError}
          </Alert>
        )}

        {!decksLoading && decks.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Create your first deck to start adding flashcards.
          </Alert>
        )}
      </Paper>

      {cardsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardsError}
        </Alert>
      )}

      <Grid container spacing={2}>
        {(cardsLoading ? Array.from({ length: 8 }).map((_, idx) => ({ __skeleton: true, id: `s-${idx}` })) : cards).map((card) => {
          if (card.__skeleton) {
            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={card.id}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading...
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          const isRevealed = revealedCardIds.has(card.id);
          const isSelected = selectedCardIds.has(card.id);
          const frontImg = card.front_image_url;
          const backImg = card.back_image_url;

          return (
            <Grid item xs={6} sm={4} md={3} lg={2} key={card.id}>
              <Card
                variant="outlined"
                sx={{
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => toggleSelected(card.id)}
                    inputProps={{ 'aria-label': 'select flashcard' }}
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCard(card);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>

                <CardActionArea onClick={() => toggleReveal(card.id)}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {card.front_text}
                    </Typography>

                    {frontImg && !isRevealed && (
                      <Box
                        component="img"
                        src={frontImg}
                        alt=""
                        sx={{
                          width: '100%',
                          maxHeight: 160,
                          objectFit: 'contain',
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: 'background.default',
                        }}
                      />
                    )}

                    {isRevealed && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        {card.back_text && (
                          <Typography variant="body2" sx={{ mb: backImg ? 1 : 0 }}>
                            {card.back_text}
                          </Typography>
                        )}
                        {backImg && (
                          <Box
                            component="img"
                            src={backImg}
                            alt=""
                            sx={{
                              width: '100%',
                              maxHeight: 160,
                              objectFit: 'contain',
                              borderRadius: 1,
                              bgcolor: 'background.default',
                            }}
                          />
                        )}
                        {!card.back_text && !backImg && (
                          <Typography variant="body2" color="text.secondary">
                            (No back content)
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {!cardsLoading && selectedDeckId && cards.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No flashcards yet. Create one or import from a CSV/XLSX.
        </Alert>
      )}

      <Dialog open={createDeckOpen} onClose={() => setCreateDeckOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create deck</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Deck name"
              value={newDeckName}
              onChange={(e) => setNewDeckName(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={newDeckDescription}
              onChange={(e) => setNewDeckDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Front language</InputLabel>
                <Select
                  value={newDeckFrontLang}
                  label="Front language"
                  onChange={(e) => setNewDeckFrontLang(e.target.value)}
                >
                  <MenuItem value="">Auto-detect</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="zh">Chinese</MenuItem>
                  <MenuItem value="ja">Japanese</MenuItem>
                  <MenuItem value="ko">Korean</MenuItem>
                  <MenuItem value="ru">Russian</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Back language</InputLabel>
                <Select
                  value={newDeckBackLang}
                  label="Back language"
                  onChange={(e) => setNewDeckBackLang(e.target.value)}
                >
                  <MenuItem value="">Auto-detect</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="zh">Chinese</MenuItem>
                  <MenuItem value="ja">Japanese</MenuItem>
                  <MenuItem value="ko">Korean</MenuItem>
                  <MenuItem value="ru">Russian</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDeckOpen(false)} disabled={creatingDeck}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateDeck} disabled={creatingDeck}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createCardOpen} onClose={() => setCreateCardOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New flashcard</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Front"
              value={newCardFront}
              onChange={(e) => setNewCardFront(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Back"
              value={newCardBack}
              onChange={(e) => setNewCardBack(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Front image URL (optional)"
              value={newCardFrontImageUrl}
              onChange={(e) => setNewCardFrontImageUrl(e.target.value)}
              fullWidth
            />
            <TextField
              label="Back image URL (optional)"
              value={newCardBackImageUrl}
              onChange={(e) => setNewCardBackImageUrl(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCardOpen(false)} disabled={creatingCard}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateCard} disabled={creatingCard}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => {
          if (importing) return;
          setImportOpen(false);
          resetImportState();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Import flashcards</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={importing}>
              Choose CSV/XLSX file
              <input
                type="file"
                hidden
                accept=".csv,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  handlePickImportFile(file);
                }}
              />
            </Button>

            {importFile && (
              <Typography variant="body2" color="text.secondary">
                {importFile.name}
              </Typography>
            )}

            {importHeaders.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="import-front-label">Front column</InputLabel>
                    <Select
                      labelId="import-front-label"
                      label="Front column"
                      value={importMapping.front}
                      onChange={(e) => setImportMapping((p) => ({ ...p, front: e.target.value }))}
                    >
                      {importHeaders.map((h) => (
                        <MenuItem key={`front-${h}`} value={h}>
                          {h || '(blank)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="import-back-label">Back column</InputLabel>
                    <Select
                      labelId="import-back-label"
                      label="Back column"
                      value={importMapping.back}
                      onChange={(e) => setImportMapping((p) => ({ ...p, back: e.target.value }))}
                    >
                      <MenuItem value="">(none)</MenuItem>
                      {importHeaders.map((h) => (
                        <MenuItem key={`back-${h}`} value={h}>
                          {h || '(blank)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="import-tags-label">Tags column</InputLabel>
                    <Select
                      labelId="import-tags-label"
                      label="Tags column"
                      value={importMapping.tags}
                      onChange={(e) => setImportMapping((p) => ({ ...p, tags: e.target.value }))}
                    >
                      <MenuItem value="">(none)</MenuItem>
                      {importHeaders.map((h) => (
                        <MenuItem key={`tags-${h}`} value={h}>
                          {h || '(blank)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="import-front-img-label">Front image URL column</InputLabel>
                    <Select
                      labelId="import-front-img-label"
                      label="Front image URL column"
                      value={importMapping.frontImageUrl}
                      onChange={(e) => setImportMapping((p) => ({ ...p, frontImageUrl: e.target.value }))}
                    >
                      <MenuItem value="">(none)</MenuItem>
                      {importHeaders.map((h) => (
                        <MenuItem key={`fimg-${h}`} value={h}>
                          {h || '(blank)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="import-back-img-label">Back image URL column</InputLabel>
                    <Select
                      labelId="import-back-img-label"
                      label="Back image URL column"
                      value={importMapping.backImageUrl}
                      onChange={(e) => setImportMapping((p) => ({ ...p, backImageUrl: e.target.value }))}
                    >
                      <MenuItem value="">(none)</MenuItem>
                      {importHeaders.map((h) => (
                        <MenuItem key={`bimg-${h}`} value={h}>
                          {h || '(blank)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {importRows.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Preview (first {Math.min(importPreviewCount, importRows.length)} rows)
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr">
                        {importHeaders.map((h, idx) => (
                          <Box
                            component="th"
                            key={`h-${idx}`}
                            sx={{ textAlign: 'left', fontSize: 12, color: 'text.secondary', pr: 2, pb: 1 }}
                          >
                            {h || '(blank)'}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {importRows.slice(0, importPreviewCount).map((row, ridx) => (
                        <Box component="tr" key={`r-${ridx}`}>
                          {(Array.isArray(row) ? row : []).map((cell, cidx) => (
                            <Box
                              component="td"
                              key={`c-${ridx}-${cidx}`}
                              sx={{ fontSize: 12, color: 'text.primary', pr: 2, pb: 0.5, verticalAlign: 'top' }}
                            >
                              {String(cell ?? '')}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (importing) return;
              setImportOpen(false);
              resetImportState();
            }}
            disabled={importing}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleImport} disabled={importing || !importFile}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(upgradePrompt)} onClose={() => setUpgradePrompt(null)} fullWidth maxWidth="xs">
        <DialogTitle>{upgradePrompt?.title || 'Upgrade to Premium'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {upgradePrompt?.message || 'Upgrade to unlock unlimited flashcards.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradePrompt(null)}>Not now</Button>
          <Button variant="contained" onClick={() => navigate('/usage?plan=yearly')}>
            See Plans
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingCard)} onClose={() => setEditingCard(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Edit flashcard</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Front"
              value={editFront}
              onChange={(e) => setEditFront(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              autoFocus
            />
            <TextField
              label="Back"
              value={editBack}
              onChange={(e) => setEditBack(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Front image URL (optional)"
              value={editFrontImg}
              onChange={(e) => setEditFrontImg(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Back image URL (optional)"
              value={editBackImg}
              onChange={(e) => setEditBackImg(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingCard(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving || !editFront.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
};

export default Flashcards;
